/**
 * IT training: the small pieces every SLA picture shares (22 Oct) — the
 * priority chip in the slide's colours and the localised duration ("30
 * minutes", "3 days"). Used by the reference at the top of the IT training
 * page and by the lesson's widgets, so both look the same.
 */

import { useMemo } from "react";

import { useLanguage, useT } from "@/i18n";

import type { SlaDuration, SlaPriority } from "./lib/sla-reference";

/**
 * Rusty's slide colours, P1 red → P5 blue, from the Norican palette. Fixed
 * rather than theme tokens: the colours ARE the meaning, in light and dark.
 * Text contrast is AA on each (white on red, navy on the rest).
 */
export const PRIORITY_LOOK: Readonly<Record<SlaPriority, string>> = {
  1: "bg-brand-red-600 text-white",
  2: "bg-brand-yellow-400 text-brand-blue-700",
  3: "bg-brand-yellow-200 text-brand-blue-700",
  4: "bg-green-500 text-brand-blue-700",
  5: "bg-cyan-500 text-brand-blue-700",
};

export function PriorityChip({
  priority,
  large = false,
}: {
  priority: SlaPriority;
  large?: boolean;
}) {
  const { t } = useT("tickets");
  return (
    <span
      className={`${PRIORITY_LOOK[priority]} inline-flex items-center justify-center rounded-md font-bold ${
        large ? "h-12 w-full text-lg" : "min-w-10 px-2 py-1 text-sm"
      }`}
    >
      {t("itAdmin.training.sla.priorityShort", { n: priority })}
    </span>
  );
}

/** "30 minutes", "1 day" in the reader's language. */
export function useDuration(): (duration: SlaDuration) => string {
  const { locale } = useLanguage();
  return useMemo(() => {
    const formats = new Map<string, Intl.NumberFormat>();
    return ({ value, unit }: SlaDuration) => {
      let format = formats.get(unit);
      if (format === undefined) {
        format = new Intl.NumberFormat(locale, { style: "unit", unit, unitDisplay: "long" });
        formats.set(unit, format);
      }
      return format.format(value);
    };
  }, [locale]);
}
